import React, { useState, useEffect } from 'react';
import { Document, Page, Text, View, StyleSheet, Svg, Rect, Path } from '@react-pdf/renderer';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { GoDownload } from 'react-icons/go';
import { useAppContext } from '../../context/AppContext';
import { useJobContext } from '../../context/JobContext';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';



// Detect browser
const isEdge = () => {
  return /Edg\//.test(navigator.userAgent);
};

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
  },
  header: {
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
    color: '#333',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    borderBottom: 1,
    borderBottomColor: '#333',
  },
  text: {
    marginBottom: 5,
    lineHeight: '1rem',
  },
  columnContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '3px 8px 8px 8px',
  },
  column: {
    width: '30%',
    marginRight: 10,
  },
  fullWidthText: {
    width: '100%',
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'left',
    paddingTop: '5px',
    paddingLeft: '8px',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  checkbox: {
    width: 12,
    height: 12,
    marginRight: 5,
  },
  downloadButtonChrome: {
    position: 'absolute',
    top: 12,
    right: 85,
    zIndex: 30,
    padding: 10,
    backgroundColor: '#3C3C3C',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.2s',
    color: 'white',
  },
  downloadButtonEdge: {
    position: 'absolute',
    top: 5,
    right: 31,
    zIndex: 30,
    padding: '6px 12px',
    backgroundColor: '#F7F7F7',
    borderRadius: '8px',
    border: '1px solid #F7F7F7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.2s',
    color: 'black',
  },
  icon: {
    fontSize: 18,
    position: 'relative',
    top: '-4px',
  },
});

// Checkbox Component
const Checkbox = ({ checked }) => (
  <Svg width={12} height={12} viewBox="0 0 12 12" style={styles.checkbox}>
    <Rect x="0" y="0" width="12" height="12" fill="none" stroke="black" strokeWidth="1" />
    {checked && (
      <Path
        d="M2 6 L5 9 L10 3"
        stroke="black"
        strokeWidth="1.5"
        fill="none"
      />
    )}
  </Svg>
);

// Resume Document Component (Mailer Information)
const ResumeDocument = ({ userData, pdfTitle }) => {
  const {
    addon1,
    addon2,
    basepromotions,
    dropship,
    dropshipname,
    jobDescription,
    permitName,
    permitAddress,
    permitAddress2,
    permitCity,
    permitState,
    permitZip,
    permitNumber,
    permitDueNetNumber,
    permitType,
    ownerCompanyName,
    ownerAddress,
    ownerAddress2,
    ownerCity,
    ownerState,
    ownerZip,
    postofficename,
    promotions,
    providerName,
    providerAddress,
    providerAddress2,
    providerCity,
    providerState,
    providerZip,
    providerEinduction,
    providerSeamless,
    no1Trays,
    growthIncentive,
    ballotbox,
    political,
    mailDate,
    uspsJobId,
  } = userData;
  const addons = [addon1, addon2];
  console.log(addons);

  // Validate mailDate before formatting
  const formattedMailDate = mailDate && isValid(new Date(mailDate)) ? format(new Date(mailDate), 'MM/dd/yyyy') : 'N/A';

  return (
    <Document title={pdfTitle}>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>{jobDescription}</Text>

        <View style={{ border: '1px solid gray', marginTop: '15px' }}>
          <View style={styles.fullWidthText}>
            <Text>Mailing Details</Text>
          </View>
          <View style={styles.columnContainer}>
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Product</Text>
              <Text style={styles.text}>{permitName || '6x9 Postcard - 14pt Cover'}</Text>
              <Text style={{ ...styles.sectionTitle, marginTop: '8px' }}>Campaign Information</Text>
              <Text>Mail Date: {formattedMailDate}</Text>
              <Text>Mail Permit: {permitNumber} </Text>
              <Text>Pay Method: {permitType} </Text>
              <Text>USPS Job Id: {uspsJobId} </Text>
            </View>

            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Post Office of Entry</Text>
              <Text>{postofficename}</Text>
              <Text style={{ ...styles.sectionTitle, marginTop: '12px' }}>Drop Ship Company</Text>
              <Text>{dropshipname}</Text>
              <Text></Text>
            </View>

            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Advanced Options</Text>
              <View style={styles.checkboxContainer}>
                <Checkbox checked={no1Trays || false} />
                <Text style={{ ...styles.text, paddingTop: '3px' }}>{"No 1' Trays (MM or EMM)"}</Text>
              </View>
              <View style={styles.checkboxContainer}>
                <Checkbox checked={growthIncentive || false} />
                <Text style={{ ...styles.text, paddingTop: '3px' }}>{'Growth Incentive Credit Redemption'}</Text>
              </View>
              <View style={styles.checkboxContainer}>
                <Checkbox checked={ballotbox || false} />
                <Text style={{ ...styles.text, paddingTop: '3px' }}>{'Ballot Mail'}</Text>
              </View>
              <View style={styles.checkboxContainer}>
                <Checkbox checked={political || false} />
                <Text style={{ ...styles.text, paddingTop: '3px' }}>{'Political Mail'}</Text>
              </View>
              <View style={styles.checkboxContainer}>
                <Checkbox checked={promotions || false} />
                <Text style={{ ...styles.text, paddingTop: '3px' }}>{'Postage Promotions'}</Text>
              </View>
              <View style={{ ...styles.checkboxContainer, marginBottom: 1 }}>
                <Text style={{ ...styles.text, paddingTop: '-8px', paddingLeft: '17px' }}>
                  {basepromotions}
                </Text>
              </View>
              <View style={{ ...styles.checkboxContainer, marginBottom: 2 }}>
                <Text style={{ ...styles.text, paddingTop: '1px' }}>Add-Ons:</Text>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: '45px', marginTop: "-17px" }}>
                <Text style={{ ...styles.text, paddingTop: '1px' }}>{addon1}</Text>
              </View>
              <View style={{ ...styles.checkboxContainer, paddingLeft: '45px', marginTop: "-9px" }}>
                <Text style={{ ...styles.text, paddingTop: '1px' }}>{addon2}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={{ border: '1px solid gray', marginTop: '15px' }}>
          <View style={styles.fullWidthText}>
            <Text>Mailer Information</Text>
          </View>
          <View style={styles.columnContainer}>
            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Permit Holder</Text>
              <Text style={styles.text}>{permitName}</Text>
              <Text style={styles.text}>{permitAddress}</Text>
              {permitAddress2 && <Text style={styles.text}>{permitAddress2}</Text>}
              <Text style={styles.text}>
                {permitCity}, {permitState} {permitZip}
              </Text>
              <Text style={{ ...styles.text, marginTop: '10px' }}>
                Permit Number: {permitNumber}
              </Text>
              <Text style={styles.text}>
                Net Due Permit #: {permitDueNetNumber}
              </Text>
            </View>

            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Mail Owner</Text>
              <Text style={styles.text}>{ownerCompanyName}</Text>
              <Text style={styles.text}>{ownerAddress}</Text>
              {ownerAddress2 && <Text style={styles.text}>{ownerAddress2}</Text>}
              <Text style={styles.text}>
                {ownerCity}, {ownerState} {ownerZip}
              </Text>
            </View>

            <View style={styles.column}>
              <Text style={styles.sectionTitle}>Mail Provider</Text>
              <Text style={styles.text}>{providerName}</Text>
              <Text style={styles.text}>{providerAddress}</Text>
              {providerAddress2 && <Text style={styles.text}>{providerAddress2}</Text>}
              <Text style={styles.text}>
                {providerCity}, {providerState} {providerZip}
              </Text>
              <Text style={{ ...styles.text, marginTop: '10px' }}>
                eInduction: {providerEinduction ? 'Yes' : 'No'}
              </Text>
              <Text style={styles.text}>
                Seamless Acceptance: {providerSeamless ? 'Yes' : 'No'}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Resume PDF Component
const ResumePDF = ({ company, userData = {}, pdfTitle = userData.permitName || 'Mailer_Information', jobName = userData.permitName || 'Mailer_Information' }) => {
  const { user } = useAppContext();
  const { formData,addjobtodb,formattedJSON,handleClick } = useJobContext();
  const [isPDFVisible, setIsPDFVisible] = useState(true);

  const [key, setKey] = useState(0);
  // Merge context data with userData prop, prioritizing userData
  const combinedData = {
    ...user,
    ...formData,
    ...userData,
  };

  // Log for debugging
  console.log('AppContext user:', user);
  console.log('JobContext info:', formData);
  console.log('userData prop:', userData);
  console.log('combinedData:', combinedData);


  const downloadFileName = `${jobName}.pdf`;
  const isEdgeBrowser = isEdge();
  const buttonStyle = isEdgeBrowser ? styles.downloadButtonEdge : styles.downloadButtonChrome;
  const buttonClass = isEdgeBrowser ? 'pdf-download-button-edge' : 'pdf-download-button-chrome';

  // Check if combinedData has valid mailDate
  const isValidData = combinedData.mailDate && isValid(new Date(combinedData.mailDate));



  



  // Reset visibility when component mounts or tab is selected
  useEffect(() => {
    console.log('ResumePDF mounted or tab selected, setting isPDFVisible to true');
   
    setKey((prev) => prev + 1);
  }, []);



  return (
    <div key={key}>
      {isValidData && (
      <div className='w-full flex place-content-end pb-5 '>
      <button
       type="button"
        className="border-1 mr-[40px] px-6 py-3 bg-gradient-to-r from-gray-400 to-gray-600 text-white rounded-full font-semibold text-base shadow-md hover:shadow-lg focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 transition-all duration-300 ease-in-out transform hover:scale-105"
        onClick={() => handleClick(company)}
      >
        Save
      </button>
      <button
       type="button"
        className="px-6 py-3 bg-gradient-to-r from-gray-400 to-gray-600 text-white rounded-full font-semibold text-base shadow-md hover:shadow-lg focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 transition-all duration-300 ease-in-out transform hover:scale-105"
         onClick={() => handleClick(company)}
      >
        Save & Submit
      </button>
      </div>
)}
      <div style={{ position: 'relative' }}>
        <style>
          {`
            .pdf-download-button-chrome:hover a {
              background-color: #5A5A5A !important;
            }
            .pdf-download-button-edge:hover a {
              background-color: #EAEAEA !important;
            }
          `}
        </style>
        {isValidData ? (
          <>
            <div className={buttonClass}>
              <PDFDownloadLink
                document={<ResumeDocument userData={combinedData} pdfTitle={pdfTitle} />}
                fileName={downloadFileName}
                style={buttonStyle}
                title={`Download ${downloadFileName}`}
              >
                <GoDownload style={styles.icon} />
              </PDFDownloadLink>
            </div>
            <PDFViewer style={{ width: '100%', height: '100vh' }}>
              <ResumeDocument userData={combinedData} pdfTitle={pdfTitle} />
            </PDFViewer>
          </>
        ) : (
          <div>Job Details are Missing. Cannot currently create a PDF. Please start at Job Details Tab.</div>
        )}
      </div>
    </div>
  );
};

export default ResumePDF;